import React, { useState, useEffect } from 'react';
import { 
  Package, Link2, Plus, Edit, Trash2, X, Save, FileText,
  Download, Eye, Mail, DollarSign, TrendingUp, CheckCircle,
  AlertCircle, Clock, Building, Layout
} from 'lucide-react';
import { apiCall } from './Api';
import InvoiceTemplateDesigner from './InvoiceTemplateDesigner';

const ServiceManagement = ({ user }) => {
  const [activeTab, setActiveTab] = useState('profiles');
  const [loading, setLoading] = useState(false);

  // Billing Profiles states
  const [billingProfiles, setBillingProfiles] = useState([]);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);

  // Service Catalog states
  const [services, setServices] = useState([]);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState(null);

  // Subscriptions states
  const [subscriptions, setSubscriptions] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);

  // Manual Invoice states
  const [showManualInvoiceForm, setShowManualInvoiceForm] = useState(false);
  const [hostsWithoutProfiles, setHostsWithoutProfiles] = useState([]);

  // Invoices states
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  // Pricing Slabs states
  const [pricingSlabs, setPricingSlabs] = useState([]);
  const [showPricingForm, setShowPricingForm] = useState(false);

  const tabs = [
    { id: 'profiles', label: 'Billing Profiles', icon: Building },
    { id: 'services', label: 'Service Catalog', icon: Package },
    { id: 'subscriptions', label: 'Subscriptions', icon: Link2 },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'pricing', label: 'Pricing Slabs', icon: DollarSign },
    { id: 'templates', label: 'Invoice Templates', icon: Layout },
    { id: 'manual-invoice', label: 'Manual Invoice', icon: Plus },
  ];

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'profiles') {
        await loadBillingProfiles();
      } else if (activeTab === 'services') {
        await loadServices();
      } else if (activeTab === 'subscriptions') {
        await loadSubscriptions();
        await loadHosts();
      } else if (activeTab === 'invoices') {
        await loadInvoices();
      } else if (activeTab === 'pricing') {
        await loadPricingSlabs();
      } else if (activeTab === 'manual-invoice') {
        await loadHosts();
        await loadServices();
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBillingProfiles = async () => {
    const response = await apiCall('/api/billing/profiles');
    if (response.ok) {
      const data = await response.json();
      setBillingProfiles(data.profiles || data);
    }
  };

  const loadServices = async () => {
    const response = await apiCall('/api/billing/services');
    if (response.ok) {
      const data = await response.json();
      setServices(data.services || []);
    }
  };

  const loadSubscriptions = async () => {
    const response = await apiCall('/api/billing/subscriptions');
    if (response.ok) {
      const data = await response.json();
      setSubscriptions(data.subscriptions || []);
    }
  };

  const loadHosts = async () => {
    const response = await apiCall('/api/postgresql-hosts');
    if (response.ok) {
      const data = await response.json();
      setHosts(data.hosts || []);
      setHostsWithoutProfiles(data.hosts || []);
    }
  };

  const loadInvoices = async () => {
    const response = await apiCall('/api/billing/invoices');
    if (response.ok) {
      const data = await response.json();
      setInvoices(data.invoices || data);
    }
  };

  const loadPricingSlabs = async () => {
    const response = await apiCall('/api/billing/services');
    if (response.ok) {
      const data = await response.json();
      const servicesData = data.services || data;
      
      const servicesWithSlabs = await Promise.all(
        servicesData.map(async (service) => {
          const slabResponse = await apiCall(`/api/billing/pricing-slabs/${service.service_code}`);
          if (slabResponse.ok) {
            const slabData = await slabResponse.json();
            return {
              ...service,
              slabs: slabData.slabs || []
            };
          }
          return { ...service, slabs: [] };
        })
      );
      
      setPricingSlabs(servicesWithSlabs);
    }
  };

  const loadHostsWithoutProfiles = async () => {
    /*const response = await apiCall('/api/billing/hosts-without-profiles');
    if (response.ok) {
      const data = await response.json();
      setHostsWithoutProfiles(data.hosts || []);
    }*/
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Service Management</h1>
        <p className="text-gray-600 mt-1">Manage services, subscriptions, invoices, and pricing</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-lg shadow-sm">
        {activeTab === 'profiles' && (
          <BillingProfilesTab
            profiles={billingProfiles}
            loading={loading}
            onRefresh={loadBillingProfiles}
            showForm={showProfileForm}
            setShowForm={setShowProfileForm}
            editingProfile={editingProfile}
            setEditingProfile={setEditingProfile}
          />
        )}

        {activeTab === 'services' && (
          <ServiceCatalogTab
            services={services}
            loading={loading}
            onRefresh={loadServices}
            showForm={showServiceForm}
            setShowForm={setShowServiceForm}
            editingService={editingService}
            setEditingService={setEditingService}
          />
        )}

        {activeTab === 'subscriptions' && (
          <SubscriptionsTab
            subscriptions={subscriptions}
            services={services}
            hosts={hosts}
            loading={loading}
            onRefresh={loadSubscriptions}
            showForm={showSubscriptionForm}
            setShowForm={setShowSubscriptionForm}
          />
        )}

        {activeTab === 'invoices' && (
          <InvoicesTab
            invoices={invoices}
            loading={loading}
            onRefresh={loadInvoices}
            selectedInvoice={selectedInvoice}
            setSelectedInvoice={setSelectedInvoice}
            showInvoiceModal={showInvoiceModal}
            setShowInvoiceModal={setShowInvoiceModal}
          />
        )}

        {activeTab === 'pricing' && (
          <PricingTab
            slabs={pricingSlabs}
            loading={loading}
            showForm={showPricingForm}
            setShowForm={setShowPricingForm}
            onRefresh={loadPricingSlabs}
          />
        )}

        {activeTab === 'templates' && (
          <InvoiceTemplateDesigner />
        )}

        {activeTab === 'manual-invoice' && (
          <ManualInvoiceTab
            services={services}
            hosts={hosts}
            loading={loading}
            showForm={showManualInvoiceForm}
            setShowForm={setShowManualInvoiceForm}
            onRefresh={loadInvoices}
          />
        )}
      </div>
    </div>
  );
};

// Service Catalog Tab Component
const ServiceCatalogTab = ({ services, loading, onRefresh, showForm, setShowForm, editingService, setEditingService }) => {
  const [formData, setFormData] = useState({
    service_code: '',
    service_name: '',
    service_description: '',
    service_category: 'messaging',
    pricing_model: 'per_unit',
    base_price: 0,
    currency: 'INR',
    unit_name: 'unit',
    tax_category: 'standard',
    hsn_sac_code: '',
    is_active: true
  });

  useEffect(() => {
    if (editingService) {
      setFormData(editingService);
      setShowForm(true);
    }
  }, [editingService]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingService 
        ? `/api/billing/services/${editingService.service_code}/update`
        : '/api/billing/services/create';
      
      const method = editingService ? 'PUT' : 'POST';
      
      const response = await apiCall(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowForm(false);
        setEditingService(null);
        resetForm();
        onRefresh();
      } else {
        const error = await response.json();
        alert(error.detail || 'Error saving service');
      }
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Failed to save service');
    }
  };

  const handleDelete = async (serviceCode) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    try {
      const response = await apiCall(`/api/billing/services/${serviceCode}/delete`, {
        method: 'DELETE'
      });

      if (response.ok) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting service:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      service_code: '',
      service_name: '',
      service_description: '',
      service_category: 'messaging',
      pricing_model: 'per_unit',
      base_price: 0,
      currency: 'INR',
      unit_name: 'unit',
      tax_category: 'standard',
      hsn_sac_code: '',
      is_active: true
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Service Catalog</h2>
        <button
          onClick={() => {
            setEditingService(null);
            resetForm();
            setShowForm(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          <span>Add Service</span>
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((service) => (
            <div key={service.service_code} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{service.service_name}</h3>
                  <p className="text-xs text-gray-500 mt-1">Code: {service.service_code}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setEditingService(service)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(service.service_code)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-3">{service.service_description}</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Category:</span>
                  <span className="font-medium">{service.service_category}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Pricing:</span>
                  <span className="font-medium">{service.pricing_model}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Base Price:</span>
                  <span className="font-medium">{service.currency} {parseFloat(service.base_price).toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Status:</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    service.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {service.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Service Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {editingService ? 'Edit Service' : 'Add New Service'}
              </h3>
              <button onClick={() => {
                setShowForm(false);
                setEditingService(null);
                resetForm();
              }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Code *
                  </label>
                  <input
                    type="text"
                    required
                    disabled={editingService}
                    value={formData.service_code}
                    onChange={(e) => setFormData({ ...formData, service_code: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., SMS_SERVICE"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.service_name}
                    onChange={(e) => setFormData({ ...formData, service_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.service_description}
                  onChange={(e) => setFormData({ ...formData, service_description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows="3"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={formData.service_category}
                    onChange={(e) => setFormData({ ...formData, service_category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="messaging">Messaging</option>
                    <option value="storage">Storage</option>
                    <option value="compute">Compute</option>
                    <option value="analytics">Analytics</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pricing Model
                  </label>
                  <select
                    value={formData.pricing_model}
                    onChange={(e) => setFormData({ ...formData, pricing_model: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="per_unit">Per Unit</option>
                    <option value="volume_based">Volume Based</option>
                    <option value="flat_rate">Flat Rate</option>
                    <option value="tiered">Tiered</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Base Price
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.base_price}
                    onChange={(e) => setFormData({ ...formData, base_price: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit Name
                  </label>
                  <input
                    type="text"
                    value={formData.unit_name}
                    onChange={(e) => setFormData({ ...formData, unit_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., message, GB, hour"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tax Category
                  </label>
                  <select
                    value={formData.tax_category}
                    onChange={(e) => setFormData({ ...formData, tax_category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="standard">Standard (18%)</option>
                    <option value="reduced">Reduced (5%)</option>
                    <option value="exempt">Exempt (0%)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    HSN/SAC Code
                  </label>
                  <input
                    type="text"
                    value={formData.hsn_sac_code}
                    onChange={(e) => setFormData({ ...formData, hsn_sac_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                  Service is active
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingService(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {editingService ? 'Update' : 'Create'} Service
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Subscriptions Tab Component
const SubscriptionsTab = ({ subscriptions, services, hosts, loading, onRefresh, showForm, setShowForm }) => {
  const [formData, setFormData] = useState({
    host_tenant_id: '',
    service_code: '',
    subscription_name: '',
    subscription_status: 'active',
    start_date: new Date().toISOString().split('T')[0],
    monthly_usage_limit: 0,
    alert_threshold_percentage: 80,
    include_in_billing: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await apiCall('/api/billing/subscriptions/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowForm(false);
        resetForm();
        onRefresh();
      } else {
        const error = await response.json();
        alert(error.detail || 'Error creating subscription');
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      alert('Failed to create subscription');
    }
  };

  const handleDelete = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;
    
    try {
      const response = await apiCall(`/api/billing/subscriptions/${subscriptionId}/cancel`, {
        method: 'DELETE'
      });

      if (response.ok) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting subscription:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      host_tenant_id: '',
      service_code: '',
      subscription_name: '',
      subscription_status: 'active',
      start_date: new Date().toISOString().split('T')[0],
      monthly_usage_limit: 0,
      alert_threshold_percentage: 80,
      include_in_billing: true
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Service Subscriptions</h2>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          <span>Add Subscription</span>
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      ) : subscriptions.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Link2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No subscriptions yet</p>
          <button
            onClick={() => setShowForm(true)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create First Subscription
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {subscriptions.map((sub) => (
            <div key={sub.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-semibold text-gray-900">{sub.service_name}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      sub.subscription_status === 'active' ? 'bg-green-100 text-green-800' :
                      sub.subscription_status === 'trial' ? 'bg-blue-100 text-blue-800' :
                      sub.subscription_status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {sub.subscription_status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{sub.subscription_name}</p>
                  <div className="grid grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Host:</span>
                      <p className="font-medium">{sub.host_tenant_id}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Start Date:</span>
                      <p className="font-medium">{new Date(sub.start_date).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Usage Limit:</span>
                      <p className="font-medium">
                        {sub.monthly_usage_limit === 0 ? 'Unlimited' : sub.monthly_usage_limit.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Billing:</span>
                      <p className="font-medium">
                        {sub.include_in_billing ? (
                          <span className="text-green-600 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" /> Enabled
                          </span>
                        ) : (
                          <span className="text-gray-500">Disabled</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(sub.id)}
                  className="text-red-600 hover:text-red-900 ml-4"
                  title="Cancel subscription"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Subscription Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Add Service Subscription</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Host *
                  </label>
                  <select
                    required
                    value={formData.host_tenant_id}
                    onChange={(e) => setFormData({ ...formData, host_tenant_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Choose a host...</option>
                    {hosts.map((host) => (
                      <option key={host.host_tenant_id} value={host.host_tenant_id}>
                        {host.host_name} ({host.host_tenant_id})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Service *
                  </label>
                  <select
                    required
                    value={formData.service_code}
                    onChange={(e) => {
                      const selectedService = services.find(s => s.service_code === e.target.value);
                      setFormData({ 
                        ...formData, 
                        service_code: e.target.value,
                        subscription_name: selectedService ? selectedService.service_name : ''
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Choose a service...</option>
                    {services.map((service) => (
                      <option key={service.service_code} value={service.service_code}>
                        {service.service_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subscription Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.subscription_name}
                  onChange={(e) => setFormData({ ...formData, subscription_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.subscription_status}
                    onChange={(e) => setFormData({ ...formData, subscription_status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="active">Active</option>
                    <option value="trial">Trial</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Monthly Usage Limit
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.monthly_usage_limit}
                    onChange={(e) => setFormData({ ...formData, monthly_usage_limit: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Set to 0 for unlimited</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Alert Threshold (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.alert_threshold_percentage}
                    onChange={(e) => setFormData({ ...formData, alert_threshold_percentage: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="include_in_billing"
                  checked={formData.include_in_billing}
                  onChange={(e) => setFormData({ ...formData, include_in_billing: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label htmlFor="include_in_billing" className="ml-2 text-sm text-gray-700">
                  Include in billing invoices
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Link2 className="w-4 h-4" />
                  Create Subscription
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Invoices Tab Component (continued in next part...)
// Invoices Tab Component  
const InvoicesTab = ({ invoices, loading, onRefresh, selectedInvoice, setSelectedInvoice, showInvoiceModal, setShowInvoiceModal }) => {
  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      viewed: 'bg-purple-100 text-purple-800',
      partially_paid: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const handleViewInvoice = async (invoice) => {
    try {
      const response = await apiCall(`/api/billing/invoices/${invoice.id}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedInvoice(data.invoice);
        setShowInvoiceModal(true);
      }
    } catch (error) {
      console.error('Error loading invoice:', error);
    }
  };

  const handleDownloadInvoice = async (invoice) => {
    try {
      const response = await apiCall(`/api/billing/invoices/${invoice.id}/pdf`, {
        method: 'GET',
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${invoice.invoice_number}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Failed to download invoice PDF');
      }
    } catch (error) {
      console.error('Error downloading invoice:', error);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Invoices</h2>
        <button
          onClick={onRefresh}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{invoice.invoice_number}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{invoice.host_tenant_id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{invoice.invoice_date}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {invoice.currency} {parseFloat(invoice.total_amount).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(invoice.invoice_status)}`}>
                      {invoice.invoice_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleViewInvoice(invoice)}
                        className="text-blue-600 hover:text-blue-800"
                        title="View"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleDownloadInvoice(invoice)}
                        className="text-green-600 hover:text-green-800"
                        title="Download PDF"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {showInvoiceModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Invoice Details</h3>
              <button onClick={() => setShowInvoiceModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Invoice Number</p>
                  <p className="font-semibold">{selectedInvoice.invoice_number}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(selectedInvoice.invoice_status)}`}>
                    {selectedInvoice.invoice_status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Customer</p>
                  <p className="font-semibold">{selectedInvoice.host_tenant_id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Invoice Date</p>
                  <p className="font-semibold">{selectedInvoice.invoice_date}</p>
                </div>
              </div>

              {/* Line Items */}
              <div>
                <h4 className="text-md font-semibold mb-3">Line Items</h4>
                <table className="min-w-full divide-y divide-gray-200 border">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Description</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Quantity</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Unit Price</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y">
                    {selectedInvoice.line_items && selectedInvoice.line_items.map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-2 text-sm">
                          <p className="font-medium">{item.service_name}</p>
                          <p className="text-gray-500">{item.description}</p>
                        </td>
                        <td className="px-4 py-2 text-sm text-right">
                          {parseFloat(item.quantity).toLocaleString()} {item.unit_name}
                        </td>
                        <td className="px-4 py-2 text-sm text-right">
                          {selectedInvoice.currency} {parseFloat(item.unit_price).toLocaleString()}
                        </td>
                        <td className="px-4 py-2 text-sm text-right font-medium">
                          {selectedInvoice.currency} {parseFloat(item.line_total).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-80 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span className="font-medium">{selectedInvoice.currency} {parseFloat(selectedInvoice.subtotal).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-base font-semibold border-t pt-2">
                    <span>Total Amount:</span>
                    <span>{selectedInvoice.currency} {parseFloat(selectedInvoice.total_amount).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t px-6 py-4 flex justify-end">
              <button
                onClick={() => setShowInvoiceModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Pricing Tab Component
const PricingTab = ({ slabs, loading, showForm, setShowForm, onRefresh }) => {
  const [formData, setFormData] = useState({
    service_code: '',
    slab_name: '',
    slab_order: 1,
    min_units: 0,
    max_units: 0,
    price_per_unit: 0,
    currency: 'INR',
    flat_fee: 0
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await apiCall('/api/billing/pricing-slabs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setShowForm(false);
        resetForm();
        onRefresh();
      }
    } catch (error) {
      console.error('Error creating pricing slab:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      service_code: '',
      slab_name: '',
      slab_order: 1,
      min_units: 0,
      max_units: 0,
      price_per_unit: 0,
      currency: 'INR',
      flat_fee: 0
    });
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Pricing Slabs</h2>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          <span>Add Slab</span>
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      ) : (
        <div className="space-y-6">
          {slabs.map((service) => (
            <div key={service.service_code} className="border rounded-lg p-4">
              <h3 className="font-semibold text-lg mb-3">{service.service_name}</h3>
              <div className="space-y-2">
                {service.slabs && service.slabs.length > 0 ? (
                  service.slabs.map((slab) => (
                    <div key={slab.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                      <div>
                        <p className="font-medium">{slab.slab_name}</p>
                        <p className="text-sm text-gray-600">
                          {slab.min_units} - {slab.max_units === 999999999 ? '∞' : slab.max_units} units
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{slab.currency} {parseFloat(slab.price_per_unit).toFixed(2)}/unit</p>
                        {slab.flat_fee > 0 && (
                          <p className="text-sm text-gray-600">+ {slab.currency} {parseFloat(slab.flat_fee).toFixed(2)} flat</p>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">No pricing slabs defined</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pricing Slab Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Add Pricing Slab</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service *
                </label>
                <select
                  required
                  value={formData.service_code}
                  onChange={(e) => setFormData({ ...formData, service_code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select service...</option>
                  {slabs.map((service) => (
                    <option key={service.service_code} value={service.service_code}>
                      {service.service_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slab Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.slab_name}
                    onChange={(e) => setFormData({ ...formData, slab_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., First 1000 units"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Order
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.slab_order}
                    onChange={(e) => setFormData({ ...formData, slab_order: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Min Units *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.min_units}
                    onChange={(e) => setFormData({ ...formData, min_units: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Units *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.max_units}
                    onChange={(e) => setFormData({ ...formData, max_units: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="999999999 for unlimited"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price per Unit *
                  </label>
                  <input
                    type="number"
                    required
                    step="0.01"
                    min="0"
                    value={formData.price_per_unit}
                    onChange={(e) => setFormData({ ...formData, price_per_unit: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Flat Fee
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.flat_fee}
                    onChange={(e) => setFormData({ ...formData, flat_fee: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Slab
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Manual Invoice Tab Component
const ManualInvoiceTab = ({ services, hosts, loading, showForm, setShowForm, onRefresh }) => {
  const [formData, setFormData] = useState({
    host_tenant_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    currency: 'INR',
    notes: '',
    terms_conditions: 'Payment due within 30 days',
    line_items: [
      {
        service_code: '',
        description: '',
        quantity: 1,
        unit_price: 0,
        tax_rate: 18
      }
    ]
  });

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, {
        service_code: '',
        description: '',
        quantity: 1,
        unit_price: 0,
        tax_rate: 18
      }]
    });
  };

  const removeLineItem = (index) => {
    const newLineItems = formData.line_items.filter((_, i) => i !== index);
    setFormData({ ...formData, line_items: newLineItems });
  };

  const updateLineItem = (index, field, value) => {
    const newLineItems = [...formData.line_items];
    newLineItems[index] = { ...newLineItems[index], [field]: value };
    setFormData({ ...formData, line_items: newLineItems });
  };

  const calculateTotal = () => {
    return formData.line_items.reduce((total, item) => {
      const subtotal = item.quantity * item.unit_price;
      const tax = subtotal * (item.tax_rate / 100);
      return total + subtotal + tax;
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await apiCall('/api/billing/invoices/manual', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('Manual invoice created successfully');
        setShowForm(false);
        onRefresh();
      } else {
        const error = await response.json();
        alert(error.detail || 'Error creating invoice');
      }
    } catch (error) {
      console.error('Error creating manual invoice:', error);
      alert('Failed to create invoice');
    }
  };

  return (
    <div className="p-6">
      <div className="text-center py-8">
        <FileText className="w-16 h-16 text-blue-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold mb-2">Create Manual Invoice</h3>
        <p className="text-gray-600 mb-6">Generate custom invoices with flexible line items</p>
        <button
          onClick={() => setShowForm(true)}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Create New Invoice
        </button>
      </div>

      {/* Manual Invoice Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Create Manual Invoice</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Customer *
                  </label>
                  <select
                    required
                    value={formData.host_tenant_id}
                    onChange={(e) => setFormData({ ...formData, host_tenant_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Select customer...</option>
                    {hosts.map((host) => (
                      <option key={host.host_tenant_id} value={host.host_tenant_id}>
                        {host.host_name || host.host_tenant_id}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.invoice_date}
                    onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Line Items *
                  </label>
                  <button
                    type="button"
                    onClick={addLineItem}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add Line Item
                  </button>
                </div>

                <div className="space-y-3">
                  {formData.line_items.map((item, index) => (
                    <div key={index} className="border rounded-lg p-4 space-y-3">
                      <div className="flex justify-between items-start">
                        <h4 className="text-sm font-medium">Line Item {index + 1}</h4>
                        {formData.line_items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeLineItem(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Service</label>
                          <select
                            value={item.service_code}
                            onChange={(e) => updateLineItem(index, 'service_code', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                          >
                            <option value="">Custom item...</option>
                            {services.map((service) => (
                              <option key={service.service_code} value={service.service_code}>
                                {service.service_name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Description *</label>
                          <input
                            type="text"
                            required
                            value={item.description}
                            onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Quantity *</label>
                          <input
                            type="number"
                            required
                            min="0"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value))}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Unit Price *</label>
                          <input
                            type="number"
                            required
                            min="0"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value))}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Tax Rate (%)</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            value={item.tax_rate}
                            onChange={(e) => updateLineItem(index, 'tax_rate', parseFloat(e.target.value))}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                          />
                        </div>
                      </div>

                      <div className="text-right text-sm">
                        <span className="text-gray-600">Subtotal: </span>
                        <span className="font-medium">
                          {formData.currency} {(item.quantity * item.unit_price * (1 + item.tax_rate / 100)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows="3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Terms & Conditions
                  </label>
                  <textarea
                    value={formData.terms_conditions}
                    onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    rows="3"
                  />
                </div>
              </div>

              <div className="flex justify-between items-center pt-4 border-t">
                <div className="text-lg font-semibold">
                  Total: {formData.currency} {calculateTotal().toFixed(2)}
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Create Invoice
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Billing Profiles Tab Component
const BillingProfilesTab = ({ profiles, loading, onRefresh, showForm, setShowForm, editingProfile, setEditingProfile }) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [hosts, setHosts] = useState([]);
  
  // Load hosts when form is shown
  useEffect(() => {
    if (showForm) {
      loadHosts();
    }
  }, [showForm]);
  
  const loadHosts = async () => {
    try {
      const response = await apiCall('/api/postgresql-hosts');
      if (response.ok) {
        const data = await response.json();
        setHosts(data.hosts || []);
      }
    } catch (error) {
      console.error('Error loading hosts:', error);
    }
  };
  
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Customer Billing Profiles</h2>
        <button
          onClick={() => {
            setEditingProfile(null);
            setShowForm(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          <span>Add Profile</span>
        </button>
      </div>
      
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      ) : profiles.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Building className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No billing profiles yet</p>
          <button
            onClick={() => setShowForm(true)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create First Profile
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {profiles.map((profile) => (
            <div key={profile.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900">{profile.company_display_name}</h3>
                  <p className="text-sm text-gray-500">{profile.host_tenant_id}</p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  profile.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {profile.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center space-x-2">
                  <DollarSign className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">Currency: {profile.billing_currency}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">Cycle: {profile.billing_cycle}</span>
                </div>
                {profile.gstin && (
                  <div className="flex items-center space-x-2">
                    <Building className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">GSTIN: {profile.gstin}</span>
                  </div>
                )}
              </div>
              
              <div className="mt-4 flex space-x-2">
                <button
                  onClick={() => {
                    setEditingProfile(profile);
                    setShowForm(true);
                  }}
                  className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm flex items-center justify-center gap-1"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    setSelectedProfile(profile);
                    setShowDetailsModal(true);
                  }}
                  className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 text-sm flex items-center justify-center gap-1"
                >
                  <Eye className="w-4 h-4" />
                  View
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {showForm && (
        <BillingProfileForm
          profile={editingProfile}
          hosts={hosts}
          onClose={() => {
            setShowForm(false);
            setEditingProfile(null);
          }}
          onSave={onRefresh}
        />
      )}
      
      {showDetailsModal && selectedProfile && (
        <ProfileDetailsModal
          profile={selectedProfile}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedProfile(null);
          }}
        />
      )}
    </div>
  );
};

// Billing Profile Form Component
const BillingProfileForm = ({ profile, hosts, onClose, onSave }) => {
  const [formData, setFormData] = useState(profile || {
    host_tenant_id: '',
    company_legal_name: '',
    company_display_name: '',
    billing_address_line1: '',
    billing_address_line2: '',
    billing_city: '',
    billing_state: '',
    billing_country: 'India',
    billing_postal_code: '',
    gstin: '',
    pan: '',
    is_registered_under_gst: false,
    state_code: '',
    place_of_supply: '',
    billing_contact_name: '',
    billing_contact_email: '',
    billing_contact_phone: '',
    billing_currency: 'INR',
    billing_cycle: 'monthly',
    billing_day_of_month: 1,
    billing_start_date: new Date().toISOString().split('T')[0],
    payment_terms_days: 30,
    payment_method: 'bank_transfer'
  });
  
  const [saving, setSaving] = useState(false);
  
  // Auto-fill company name when host is selected
  const handleHostSelect = (e) => {
    const selectedHostId = e.target.value;
    const selectedHost = hosts.find(h => h.host_tenant_id === selectedHostId);
    
    if (selectedHost) {
      setFormData({
        ...formData,
        host_tenant_id: selectedHostId,
        company_legal_name: selectedHost.host_name || selectedHostId,
        company_display_name: selectedHost.host_name || selectedHostId
      });
    } else {
      setFormData({
        ...formData,
        host_tenant_id: selectedHostId
      });
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const response = await apiCall('/api/billing/profiles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        onSave();
        onClose();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save billing profile');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error saving profile');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            {profile ? 'Edit' : 'Create'} Billing Profile
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Company Information */}
          <div>
            <h4 className="text-md font-semibold mb-4 text-gray-900">Company Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Host Tenant ID *
                </label>
                {profile ? (
                  // Editing existing profile - show as disabled input
                  <input
                    type="text"
                    required
                    disabled
                    value={formData.host_tenant_id}
                    className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-600"
                  />
                ) : (
                  // Creating new profile - show dropdown
                  <select
                    required
                    value={formData.host_tenant_id}
                    onChange={handleHostSelect}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a host...</option>
                    {hosts.map((host) => (
                      <option key={host.host_tenant_id} value={host.host_tenant_id}>
                        {host.host_name} ({host.host_tenant_id})
                      </option>
                    ))}
                  </select>
                )}
                {!profile && hosts.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">Loading hosts...</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Legal Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.company_legal_name}
                  onChange={(e) => setFormData({...formData, company_legal_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Auto-filled from host or enter manually"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.company_display_name}
                  onChange={(e) => setFormData({...formData, company_display_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Auto-filled from host or enter manually"
                />
              </div>
            </div>
          </div>
          
          {/* Billing Address */}
          <div>
            <h4 className="text-md font-semibold mb-4 text-gray-900">Billing Address</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address Line 1 *
                </label>
                <input
                  type="text"
                  required
                  value={formData.billing_address_line1}
                  onChange={(e) => setFormData({...formData, billing_address_line1: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address Line 2
                </label>
                <input
                  type="text"
                  value={formData.billing_address_line2}
                  onChange={(e) => setFormData({...formData, billing_address_line2: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                <input
                  type="text"
                  required
                  value={formData.billing_city}
                  onChange={(e) => setFormData({...formData, billing_city: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                <input
                  type="text"
                  required
                  value={formData.billing_state}
                  onChange={(e) => setFormData({...formData, billing_state: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code *</label>
                <input
                  type="text"
                  required
                  value={formData.billing_postal_code}
                  onChange={(e) => setFormData({...formData, billing_postal_code: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <input
                  type="text"
                  value={formData.billing_country}
                  onChange={(e) => setFormData({...formData, billing_country: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
          
          {/* Tax Information */}
          <div>
            <h4 className="text-md font-semibold mb-4 text-gray-900">Tax Information (GST)</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
                <input
                  type="text"
                  value={formData.gstin}
                  onChange={(e) => setFormData({...formData, gstin: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="27AABCU9603R1ZM"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PAN</label>
                <input
                  type="text"
                  value={formData.pan}
                  onChange={(e) => setFormData({...formData, pan: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="AABCU9603R"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State Code</label>
                <input
                  type="text"
                  value={formData.state_code}
                  onChange={(e) => setFormData({...formData, state_code: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="27"
                />
              </div>
              <div className="flex items-center pt-6">
                <input
                  type="checkbox"
                  id="is_registered_under_gst"
                  checked={formData.is_registered_under_gst}
                  onChange={(e) => setFormData({...formData, is_registered_under_gst: e.target.checked})}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <label htmlFor="is_registered_under_gst" className="ml-2 text-sm text-gray-700">
                  Registered under GST
                </label>
              </div>
            </div>
          </div>
          
          {/* Contact Information */}
          <div>
            <h4 className="text-md font-semibold mb-4 text-gray-900">Contact Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.billing_contact_name}
                  onChange={(e) => setFormData({...formData, billing_contact_name: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.billing_contact_email}
                  onChange={(e) => setFormData({...formData, billing_contact_email: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Phone *
                </label>
                <input
                  type="tel"
                  required
                  value={formData.billing_contact_phone}
                  onChange={(e) => setFormData({...formData, billing_contact_phone: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
          
          {/* Billing Configuration */}
          <div>
            <h4 className="text-md font-semibold mb-4 text-gray-900">Billing Configuration</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                <select
                  value={formData.billing_currency}
                  onChange={(e) => setFormData({...formData, billing_currency: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="INR">INR - Indian Rupee</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Billing Cycle</label>
                <select
                  value={formData.billing_cycle}
                  onChange={(e) => setFormData({...formData, billing_cycle: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="semi_annual">Semi-Annual</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Billing Day
                </label>
                <input
                  type="number"
                  min="1"
                  max="28"
                  value={formData.billing_day_of_month}
                  onChange={(e) => setFormData({...formData, billing_day_of_month: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Billing Start Date *
                </label>
                <input
                  type="date"
                  required
                  value={formData.billing_start_date}
                  onChange={(e) => setFormData({...formData, billing_start_date: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Terms (Days)
                </label>
                <input
                  type="number"
                  value={formData.payment_terms_days}
                  onChange={(e) => setFormData({...formData, payment_terms_days: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select
                  value={formData.payment_method}
                  onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="upi">UPI</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : (profile ? 'Update' : 'Create') + ' Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Profile Details Modal
const ProfileDetailsModal = ({ profile, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h3 className="text-lg font-semibold">Profile Details</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          <div>
            <h4 className="text-md font-semibold mb-3">Company Information</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-500">Host Tenant ID</p>
                <p className="font-medium">{profile.host_tenant_id}</p>
              </div>
              <div>
                <p className="text-gray-500">Legal Name</p>
                <p className="font-medium">{profile.company_legal_name}</p>
              </div>
              <div>
                <p className="text-gray-500">Display Name</p>
                <p className="font-medium">{profile.company_display_name}</p>
              </div>
              <div>
                <p className="text-gray-500">Status</p>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  profile.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {profile.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-md font-semibold mb-3">Billing Address</h4>
            <p className="text-sm">{profile.billing_address_line1}</p>
            {profile.billing_address_line2 && <p className="text-sm">{profile.billing_address_line2}</p>}
            <p className="text-sm">{profile.billing_city}, {profile.billing_state} {profile.billing_postal_code}</p>
            <p className="text-sm">{profile.billing_country}</p>
          </div>

          <div>
            <h4 className="text-md font-semibold mb-3">Tax Information</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {profile.gstin && (
                <div>
                  <p className="text-gray-500">GSTIN</p>
                  <p className="font-medium">{profile.gstin}</p>
                </div>
              )}
              {profile.pan && (
                <div>
                  <p className="text-gray-500">PAN</p>
                  <p className="font-medium">{profile.pan}</p>
                </div>
              )}
            </div>
          </div>

          <div>
            <h4 className="text-md font-semibold mb-3">Contact Information</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-500">Contact Name</p>
                <p className="font-medium">{profile.billing_contact_name}</p>
              </div>
              <div>
                <p className="text-gray-500">Email</p>
                <p className="font-medium">{profile.billing_contact_email}</p>
              </div>
              <div>
                <p className="text-gray-500">Phone</p>
                <p className="font-medium">{profile.billing_contact_phone}</p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-md font-semibold mb-3">Billing Configuration</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-gray-500">Currency</p>
                <p className="font-medium">{profile.billing_currency}</p>
              </div>
              <div>
                <p className="text-gray-500">Billing Cycle</p>
                <p className="font-medium">{profile.billing_cycle}</p>
              </div>
              <div>
                <p className="text-gray-500">Billing Day</p>
                <p className="font-medium">Day {profile.billing_day_of_month} of month</p>
              </div>
              <div>
                <p className="text-gray-500">Payment Terms</p>
                <p className="font-medium">{profile.payment_terms_days} days</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ServiceManagement;